/**
 * TransactionsPage (FE-2, §7.1). The filtered transaction ledger: URL-synced
 * filters drive an offset/limit infinite list; the header hosts the CSV export
 * of the exact same filtered view. A `contract_id` filter (arriving via the
 * „Verlauf"-link from `/vertraege`, Phase 6 §6.2) additionally surfaces a
 * removable „Vertrag: <name>"-chip below the filter bar.
 */
import { X } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { useContracts } from '../lib/hooks/useContracts.ts';
import { useTransactionFilters, useTransactionsList } from '../lib/hooks/useTransactions.ts';
import { ExportButton } from './transactions/ExportButton.tsx';
import { TransactionFilters } from './transactions/TransactionFilters.tsx';
import { TransactionList } from './transactions/TransactionList.tsx';

export default function TransactionsPage() {
  const { filters, patch, reset } = useTransactionFilters();
  const list = useTransactionsList(filters);
  const { data: contracts } = useContracts();

  const contractId = filters.contract_id;
  const contractName =
    contractId !== undefined
      ? (contracts?.find((c) => c.id === contractId)?.name ?? `#${contractId}`)
      : undefined;

  return (
    <div>
      <PageHeader
        title="Transaktionen"
        subtitle="Alle Buchungen filtern, kategorisieren und exportieren."
        actions={<ExportButton filters={filters} disabled={list.total === 0} />}
      />
      <TransactionFilters filters={filters} patch={patch} reset={reset} />
      {contractName !== undefined ? (
        <div className="px-4 pt-3 sm:px-6">
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Vertrag: {contractName}
            <button
              type="button"
              onClick={() => patch({ contract_id: undefined })}
              aria-label="Vertragsfilter entfernen"
              className="text-slate-400 transition-colors duration-150 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
      ) : null}
      <TransactionList list={list} />
    </div>
  );
}
